import { create } from "zustand";
import type { User, Session } from "@supabase/supabase-js";

import { supabase } from "@/lib/supabase";
import { fetchSession } from "../services/authApi";
import useSessionStore from "./sessionStore";

export type AuthUser = {
  email: string;
  name?: string | null;
  picture?: string | null;
  provider: string;
  role?: "agent" | "admin" | "finance" | "super_admin" | "manager" | "client";
  id?: string;
};

type AuthState = {
  status: "idle" | "loading" | "ready";
  isAuthenticated: boolean;
  accessToken: string | null;
  user: AuthUser | null;
  error?: string;
  showRoleErrorDialog: boolean;
  hasRoleError: boolean; // Track if we've already encountered a role error to prevent retries
  initialize: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithEmailPassword: (email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  checkEmailVerification: () => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  closeRoleErrorDialog: () => void;
};

// Helper to convert Supabase User to AuthUser
const mapSupabaseUserToAuthUser = (supabaseUser: User | null): AuthUser | null => {
  if (!supabaseUser) return null;

  return {
    id: supabaseUser.id,
    email: supabaseUser.email || "",
    name: supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name || null,
    picture: supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.picture || null,
    provider: supabaseUser.app_metadata?.provider || (supabaseUser.email ? "email" : "google"),
    role: supabaseUser.user_metadata?.role || undefined,
  };
};

// Helper to update state from Supabase session
// Also fetches role from backend API
const updateStateFromSession = async (session: Session | null, set: (state: Partial<AuthState>) => void, get: () => AuthState) => {
  // If we've already encountered a role error, don't retry
  if (get().hasRoleError) {
    return;
  }

  if (session?.user) {
    const authUser = mapSupabaseUserToAuthUser(session.user);
    if (!authUser) {
      // Should not happen if session.user exists, but handle gracefully
      set({
        accessToken: null,
        isAuthenticated: false,
        user: null,
        status: "ready",
        error: "Failed to map user data.",
      });
      return;
    }
    
    // Fetch role from backend API (role is stored in database, not Supabase metadata)
    try {
      const sessionResponse = await fetchSession(session.access_token);
      if (sessionResponse.user?.role) {
        authUser.role = sessionResponse.user.role;
      }
    } catch (error) {
      // Check if it's a 403 Forbidden error (role mismatch)
      const isRoleError = (error as any)?.status === 403;
      
      // Only log the error once to avoid console spam
      if (isRoleError && !get().hasRoleError) {
      console.error("Failed to fetch role from backend:", error);
      }
      
      // If fetching the role fails (e.g., 403 Forbidden), treat as auth failure
      // This prevents redirect loops by ensuring isAuthenticated is false
      set({
        accessToken: null,
        isAuthenticated: false,
        user: null,
        status: "ready",
        error: isRoleError 
          ? (error as Error).message || "You do not have permission to access this role."
          : "Authentication failed: Could not verify user role.",
        showRoleErrorDialog: isRoleError, // Show dialog for role errors
        hasRoleError: isRoleError, // Mark that we've encountered a role error
      });
      // Also clear the problematic role selection
      localStorage.removeItem("selectedRole");
      await supabase.auth.signOut(); // Ensure Supabase session is cleared
      return; // Stop further processing
    }
    
    set({
      accessToken: session.access_token,
      isAuthenticated: true,
      user: authUser,
      status: "ready",
      error: undefined,
    });
  } else {
    set({
      accessToken: null,
      isAuthenticated: false,
      user: null,
      status: "ready",
      error: undefined,
        showRoleErrorDialog: false,
        hasRoleError: false,
    });
  }
};

const useAuthStore = create<AuthState>((set, get) => {
  // Set up auth state change listener
  supabase.auth.onAuthStateChange(async (event: string, session: Session | null) => {
    // Check if this is from an invitation link (type=invite in URL hash)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isInvitationLink = hashParams.get('type') === 'invite';
    
    // If user signed in via invitation link, redirect to password setup IMMEDIATELY
    // This must happen BEFORE updateStateFromSession to prevent navigation to dashboard
    if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && isInvitationLink && session?.user) {
      // Preserve the hash for password setup page
      const currentHash = window.location.hash;
      
      // Use location.replace to redirect immediately without adding to history
      window.location.replace('/set-password' + currentHash);
      return; // Don't process the sign-in event
    }
    
    if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
      await updateStateFromSession(session, set, get);
    } else if (event === "SIGNED_OUT") {
      // Preserve showRoleErrorDialog state if it's currently true (don't close dialog on signOut)
      const currentState = get();
      set({
        accessToken: null,
        isAuthenticated: false,
        user: null,
        status: "ready",
        error: undefined,
        showRoleErrorDialog: currentState.showRoleErrorDialog, // Preserve dialog state
        hasRoleError: false, // Reset role error flag on sign out
      });
      useSessionStore.getState().reset();
    }
  });

  return {
  status: "idle",
  isAuthenticated: false,
  accessToken: null,
  user: null,
  error: undefined,
  showRoleErrorDialog: false,
  hasRoleError: false,
  initialize: async () => {
    if (get().status === "loading") return;
    // Note: We allow initialize() to check the session even if hasRoleError is true
    // The updateStateFromSession function will prevent retries if hasRoleError is still true
    // This allows users to recover from error states if conditions have changed
    set({ status: "loading" });

    // Check if this is an invitation link (type=invite in URL hash)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isInvitationLink = hashParams.get('type') === 'invite';

    try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        // If this is an invitation link, DON'T process the session
        // Just redirect to password setup page
        if (isInvitationLink && session?.user) {
          // Redirect to password setup page with the hash
          const currentHash = window.location.hash;
          
          // Use location.replace to redirect immediately
          window.location.replace('/set-password' + currentHash);
          return;
        }
        
        if (error) {
          console.error("Failed to get session:", error);
      set({
        status: "ready",
            isAuthenticated: false,
            accessToken: null,
            user: null,
        error: undefined,
      });
          return;
        }

        await updateStateFromSession(session, set, get);
    } catch (error) {
        console.error("Error initializing auth:", error);
      set({
        status: "ready",
        isAuthenticated: false,
        accessToken: null,
        user: null,
        error: undefined,
      });
    }
  },
    loginWithGoogle: async () => {
    try {
      set({ status: "loading", error: undefined });
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: `${window.location.origin}/`,
          },
        });

        if (error) {
          throw error;
        }

        // OAuth flow will redirect, state will be updated via onAuthStateChange
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to sign in with Google";
      set({ error: message, status: "ready", isAuthenticated: false });
      throw error;
    }
  },
    loginWithEmailPassword: async (email: string, password: string) => {
      try {
        set({ status: "loading", error: undefined });

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          // Check if error is due to unverified email
          if (error.message.includes("Email not confirmed") || error.message.includes("email_not_confirmed")) {
            throw new Error("Please verify your email before signing in. Check your inbox for the verification link.");
          }
          throw error;
        }

        // Check if email is verified
        if (data.user && !data.user.email_confirmed_at) {
          await supabase.auth.signOut();
          throw new Error("Please verify your email before signing in. Check your inbox for the verification link.");
        }

        // Session will be updated via onAuthStateChange
        // But we can also update immediately
        if (data.session) {
          await updateStateFromSession(data.session, set, get);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to sign in with email and password";
        set({ error: message, status: "ready", isAuthenticated: false });
        throw error;
      }
    },
    forgotPassword: async (email: string) => {
      try {
        set({ error: undefined });
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) {
          throw error;
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to send password reset email";
        set({ error: message });
        throw error;
      }
    },
    resendVerificationEmail: async (email: string) => {
      try {
        set({ error: undefined });
        const { error } = await supabase.auth.resend({
          type: "signup",
          email: email,
        });

        if (error) {
          throw error;
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to resend verification email";
        set({ error: message });
        throw error;
      }
    },
    checkEmailVerification: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        return user?.email_confirmed_at !== null && user?.email_confirmed_at !== undefined;
      } catch (error) {
        console.error("Error checking email verification:", error);
        return false;
      }
    },
    logout: async () => {
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.error("Error signing out:", error);
        }
        // Clear selected role from localStorage on logout
        localStorage.removeItem("selectedRole");
        useSessionStore.getState().reset();
        set({
          isAuthenticated: false,
          accessToken: null,
          user: null,
          error: undefined,
          showRoleErrorDialog: false,
        hasRoleError: false,
          status: "ready",
        });
      } catch (error) {
        console.error("Error during logout:", error);
        // Still reset state even if signOut fails
        localStorage.removeItem("selectedRole");
        useSessionStore.getState().reset();
        set({
          isAuthenticated: false,
          accessToken: null,
          user: null,
          error: undefined,
          showRoleErrorDialog: false,
        hasRoleError: false,
          status: "ready",
        });
      }
  },
  clearError: () => set({ error: undefined }),
  closeRoleErrorDialog: () => set({ showRoleErrorDialog: false, hasRoleError: false, error: undefined }),
  };
});

export default useAuthStore;

